import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { DateTime } from "luxon";

import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useStockCheckReports from "../../data/useStockCheckReports";
import { IStockCheckReportSummary } from "../../models/stockCheck/IStockCheckReport";
import formatDateTime from "../../utils/formatDateTime";

const colHelper = createColumnHelper<IStockCheckReportSummary>();

const cols = [
  colHelper.accessor("completed", {
    header: "Completed",
    cell: (info) => formatDateTime(info.getValue(), DateTime.DATETIME_SHORT),
  }),
  colHelper.accessor("warehouseLocationName", {
    header: "Shelf",
  }),
  colHelper.accessor("itemCount", {
    header: "Parts",
  }),
  colHelper.accessor("completedByEmail", {
    header: "Completed by",
    cell: (info) => info.getValue() || "—",
  }),
];

export default function StockCheckReports() {
  const { setInfo } = usePageNameContext();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { reports } = useStockCheckReports(page, 25);

  useEffect(() => {
    setInfo({
      name: "Stock Check Report",
      desc: "Stock checks saved from the yard app",
    });
  }, [setInfo]);

  const data = reports.data?.data;
  const rows = data?.rows ?? [];
  const totalPages = data?.totalPages ?? 0;

  const table = useReactTable({
    data: rows,
    columns: cols,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: totalPages,
  });

  if (reports.isLoading) {
    return <LoadingWheel />;
  }

  return (
    <div className="mt-4 bg-white overflow-auto">
      {rows.length === 0 ? (
        <p className="px-4 py-6 text-sm text-gray-500">
          No stock checks have been saved yet. The yard app writes a report here when a team
          creates a stock check, scans a shelf, then saves the parts on it.
        </p>
      ) : (
        <>
          <table className="min-w-full overflow-scroll divide-y border divide-gray-300">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="bg-primary-50 text-gray-800">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      scope="col"
                      className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left"
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <Fragment key={row.id}>
                  <tr
                    onClick={() => navigate(`/stock-check-report/${row.original.id}`)}
                    className="hover:bg-gray-100 hover:cursor-pointer"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-3 py-3 border text-sm">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="flex justify-between items-center gap-2 py-4 px-4">
              <div className="text-sm text-gray-600">{data?.total ?? 0} stock checks</div>
              <div className="flex items-center gap-2">
                <ButtonInput
                  label="<"
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                  disabled={page <= 1}
                  classes="text-xl"
                />
                <span className="text-sm">
                  Page {page} of {totalPages}
                </span>
                <ButtonInput
                  label=">"
                  onClick={() => setPage((current) => current + 1)}
                  disabled={page >= totalPages}
                  classes="text-xl"
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
